// Настройки (К-14, FR-ACC-01…03): профили детей, лимит сессии, звук, тема; вход по email (привязка или слияние);
// удаление аккаунта и всех данных ребёнка.
import { useState } from "react";
import { useNavigate } from "react-router";
import { TopBar } from "../components/Shell.tsx";
import { ApiError, OfflineError, type Tokens } from "../lib/api.ts";
import { ageOf, stageForAge } from "../lib/content/catalog.ts";
import { db, type Child, type Stage } from "../lib/db.ts";
import { upsertChild } from "../lib/events.ts";
import { uuidv7 } from "../lib/uuid.ts";
import { activeChild, api, reloadLocal, saveSettings, sync, useApp } from "../state/app.ts";
import { t, years } from "../i18n/ru.ts";

function errText(e: unknown): string {
  if (e instanceof OfflineError) return t.errors.offline;
  if (e instanceof ApiError) return e.status === 429 ? t.errors.rateLimited : e.problem.title;
  return t.errors.generic;
}

function ChildForm({ child, onSaved }: { child?: Child; onSaved: () => void }) {
  const content = useApp((s) => s.content);
  const year = new Date().getFullYear();
  const [nickname, setNickname] = useState(child?.nickname ?? "");
  const [birthYear, setBirthYear] = useState(child?.birthYear ?? year - 5);
  const [stage, setStage] = useState<Stage>(child?.stage ?? stageForAge(ageOf(year - 5)));
  const [format, setFormat] = useState(child?.formatCode ?? "1v1");
  const stages = content?.dictionaries.stages ?? {};
  return (
    <form
      className="card form"
      onSubmit={(e) => {
        e.preventDefault();
        void upsertChild({ id: child?.id ?? uuidv7(), nickname: nickname.trim() || "Футболист", birthYear, stage, formatCode: format, updatedAt: Date.now() }).then(onSaved);
      }}
    >
      <label className="field">
        <span>{t.onboarding.nickname}</span>
        <input value={nickname} maxLength={40} onChange={(e) => setNickname(e.target.value)} />
      </label>
      <div className="row">
        <label className="field">
          <span>{t.onboarding.birthYear}</span>
          <select value={birthYear} onChange={(e) => setBirthYear(Number(e.target.value))}>
            {Array.from({ length: 14 }, (_, i) => year - 3 - i).map((y) => (
              <option key={y} value={y}>
                {y} ({years(year - y)})
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t.onboarding.stage}</span>
          <select value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
            {Object.entries(stages).map(([k, v]) => (
              <option key={k} value={k}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t.onboarding.format}</span>
          <select value={format} onChange={(e) => setFormat(e.target.value)}>
            {Object.values(content?.dictionaries.formats ?? {})
              .sort((a, b) => a.outfieldPlayers - b.outfieldPlayers || Number(a.goalkeeper) - Number(b.goalkeeper))
              .map((f) => (
              <option key={f.code} value={f.code}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button className="btn btn--primary">{t.common.save}</button>
    </form>
  );
}

function AccountBox() {
  const account = useApp((s) => s.account);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setMsg(null);
    try {
      await f();
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!account.anonymous) return <p id="account">{t.settings.loggedIn(account.email ?? "")}</p>;
  return (
    <div id="account" className="card form">
      <p>{t.settings.anonymous}</p>
      {!sent ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await api.request("POST", "/v1/auth/email/start", { email }, { auth: false });
              setSent(true);
              setMsg(t.settings.codeSent);
            });
          }}
        >
          <label className="field">
            <span>{t.settings.email}</span>
            <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <button className="btn btn--primary" disabled={busy}>
            {t.settings.sendCode}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await api.ensureDevice();
              const r = await api.request<Tokens & { merged: boolean; account: { id: string; email: string } }>("POST", "/v1/auth/email/verify", { email, code });
              await db.set("tokens", { access_token: r.access_token, refresh_token: r.refresh_token });
              const acc = { id: r.account.id, email: r.account.email, anonymous: false };
              await db.set("account", acc);
              await db.set("accountId", r.account.id);
              useApp.setState({ account: acc });
              // Аккаунт устройства мог смениться — забрать все его события заново.
              await sync.resetCursor();
              await sync.syncNow();
              await reloadLocal();
              setMsg(r.merged ? t.settings.merged : t.settings.bound);
            });
          }}
        >
          <label className="field">
            <span>{t.settings.code}</span>
            <input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <button className="btn btn--primary" disabled={busy}>
            {t.settings.verify}
          </button>
        </form>
      )}
      {msg ? <p role="status">{msg}</p> : null}
    </div>
  );
}

function DeleteBox() {
  const account = useApp((s) => s.account);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const del = async () => {
    try {
      if (!account.anonymous && !codeSent) {
        await api.request("POST", "/v1/auth/email/start", { email: account.email }, { auth: false });
        setCodeSent(true);
        setMsg(t.settings.codeSent);
        return;
      }
      if (await api.tokens()) await api.request("DELETE", "/v1/me", account.anonymous ? undefined : { code });
      await db.delete();
      if ("caches" in window) await caches.delete("brovka-content");
      setMsg(t.settings.deleted);
      setTimeout(() => location.replace("/"), 800);
    } catch (e) {
      setMsg(errText(e));
    }
  };
  return (
    <div className="card danger">
      <h2>{t.settings.deleteTitle}</h2>
      <p className="muted">{t.settings.deleteText}</p>
      {!open ? (
        <button className="btn btn--danger" onClick={() => setOpen(true)}>
          {t.settings.deleteTitle}
        </button>
      ) : (
        <>
          {!account.anonymous ? <p className="muted small">{t.settings.deleteCode}</p> : null}
          {codeSent ? (
            <label className="field">
              <span>{t.settings.code}</span>
              <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
          ) : null}
          <button className="btn btn--danger" onClick={() => void del()}>
            {t.settings.deleteConfirm}
          </button>
        </>
      )}
      {msg ? <p role="status">{msg}</p> : null}
    </div>
  );
}

export function Settings() {
  const nav = useNavigate();
  const { settings, children, content } = useApp();
  const child = activeChild();
  const [adding, setAdding] = useState(false);
  return (
    <div className="page settings">
      <TopBar title={t.settings.title} back={() => nav(-1)} />
      <section>
        <h2>{t.settings.child}</h2>
        {children.length > 1 ? (
          <div className="segmented">
            {children.map((c) => (
              <button key={c.id} className={c.id === child?.id ? "is-active" : ""} onClick={() => void db.set("activeChildId", c.id).then(reloadLocal)}>
                {c.nickname}
              </button>
            ))}
          </div>
        ) : null}
        {child ? <ChildForm key={child.id} child={child} onSaved={() => void reloadLocal()} /> : null}
        {adding ? (
          <ChildForm onSaved={() => (setAdding(false), void reloadLocal())} />
        ) : (
          <button className="btn" onClick={() => setAdding(true)}>
            + {t.settings.addChild}
          </button>
        )}
      </section>
      <section className="card form">
        <label className="field">
          <span>{t.settings.sessionLimit}</span>
          <select value={settings.sessionLimitMin} onChange={(e) => void saveSettings({ sessionLimitMin: Number(e.target.value) })}>
            {[5, 10, 15, 20].map((m) => (
              <option key={m} value={m}>
                {t.common.minutes(m)}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={settings.sound} onChange={(e) => void saveSettings({ sound: e.target.checked })} />
          <span>{t.settings.sound}</span>
        </label>
        <label className="field">
          <span>{t.settings.theme}</span>
          <select value={settings.theme} onChange={(e) => void saveSettings({ theme: e.target.value as "system" | "light" | "dark" })}>
            {Object.entries(t.settings.themes).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
      </section>
      <section>
        <h2>{t.settings.account}</h2>
        <AccountBox />
        <button className="btn" onClick={() => void sync.syncNow().then(reloadLocal).catch(() => {})}>
          {t.settings.syncNow}
        </button>
      </section>
      <DeleteBox />
      <p className="muted small">{t.settings.version(__APP_VERSION__, content?.releaseId ?? "—")}</p>
    </div>
  );
}
